import { combineReducers, Reducer } from 'redux';
import {
  FETCH_PROJECT_MAIL_FOLDERS_REQUEST,
  FETCH_PROJECT_MAIL_FOLDERS_ROLLBACK,
  FETCH_PROJECT_MAIL_FOLDERS_COMMIT,
  UPDATE_UNREAD_ITEM_COUNT_PROJECT,
  UPDATE_ACTIVE_MAILFOLDER_PROJECT,
  SET_OPEN_MAILFOLDER_KEYS_PROJECT,
  ADD_OPEN_MAILFOLDER_KEY_PROJECT,
  RENAME_MAIL_FOLDER_COMMIT,
  RENAME_MAIL_FOLDER_ROLLBACK,
} from '../../actions/projects/mailFolders';
import { MailFolder } from '../../../../models/MailFolder';
import { MailFolderId, ProjectId } from '../../../../models/Types';
import { CLEAR_PRIO_CACHE } from '../../../../actions';
import {
  ActiveMailFolderAction,
  MailFolderByMailFolderId,
  MailFolderIdEntry,
} from '../../actions/types';
import { distinct } from '../../../../util';
import {
  COPY_MAIL_TO_PROJECT_PROJECT_COMMIT,
  MARK_AS_READ_COMMIT,
  MOVE_MESSAGE_PROJECT_COMMIT,
  WS_REVERT_MESSAGE_DELETE_PROJECT,
  WS_REVERT_MESSAGE_MOVE_PROJECT,
} from '../../actions/projects/messages';
import { COPY_MAIL_ME_TO_PROJECT_COMMIT } from '../../actions/me/messagesMe';
import { SAGA_REBUILD } from '../../../../util/sagas';
import { ReduxAction } from '../../../../models/Redux';
import { WS_EMAIL_PROJECT_CREATED } from '../../actions/ws';
import { DELETE_LOCAL_MESSAGES } from '../../actions/actionControllers/messageActionController';
import { Message } from '../../../../models/Message';

export interface MailFoldersState {
  byId: MailFolderByMailFolderId;
  ids: ByProjectId;
  meta: MailFoldersMeta;
  activeMailFolder: ActiveMailFolderState;
  openMailFolderKeys: OpenMailFolderKeysState;
}

const reducerFunction = (
  mailFolders: Array<MailFolder>,
  state: MailFolderByMailFolderId
) =>
  mailFolders.reduce((map, item) => {
    const { childFolder: _, ...rest } = item;
    return {
      ...(item.childFolder ? reducerFunction(item.childFolder, map) : map),
      [item.id]: rest,
    };
  }, state);

const byId: Reducer<MailFolderByMailFolderId, any> = (state = {}, action) => {
  switch (action.type) {
    case FETCH_PROJECT_MAIL_FOLDERS_COMMIT: {
      const { payload } = action;
      return reducerFunction(payload as Array<MailFolder>, { ...state });
    }
    case UPDATE_UNREAD_ITEM_COUNT_PROJECT: {
      const { mailFolderId, itemCount, option } = action;
      const mailFolder = state[mailFolderId];
      return {
        ...state,
        [mailFolderId]: {
          ...mailFolder,
          unreadItemCount:
            option === 'add'
              ? (mailFolder?.unreadItemCount ?? 0) + itemCount
              : (mailFolder?.unreadItemCount ?? 0) > 0
              ? (mailFolder?.unreadItemCount ?? 0) - itemCount
              : 0,
        },
      };
    }

    case MOVE_MESSAGE_PROJECT_COMMIT: {
      const {
        meta: { destinationId, originId, unreadItemCount, inboxFolderId },
      } = action;
      const originMailFolder =
        state[originId === 'inbox' ? inboxFolderId : originId];
      const destinationMailFolder =
        state[destinationId === 'inbox' ? inboxFolderId : destinationId];
      if (unreadItemCount === 0) {
        return state;
      }
      return {
        ...state,
        ...(originMailFolder
          ? {
              [originMailFolder.id]: {
                ...originMailFolder,
                unreadItemCount:
                  originMailFolder?.unreadItemCount > 0
                    ? originMailFolder?.unreadItemCount - unreadItemCount
                    : 0,
              },
            }
          : {}),
        ...(destinationMailFolder
          ? {
              [destinationMailFolder.id]: {
                ...destinationMailFolder,
                unreadItemCount:
                  destinationMailFolder?.unreadItemCount >= 0
                    ? destinationMailFolder?.unreadItemCount + unreadItemCount
                    : 0,
              },
            }
          : {}),
      };
    }

    case COPY_MAIL_TO_PROJECT_PROJECT_COMMIT: {
      const {
        meta: { mailFolderId, destinationMailFolderId, messages },
        payload,
      } = action;
      const originMailFolder =
        state[
          mailFolderId === 'inbox' ? messages[0]?.parentFolderId : mailFolderId
        ];
      const destinationMailFolder =
        state[
          destinationMailFolderId === 'inbox'
            ? payload[0]?.parentFolderId
            : destinationMailFolderId
        ];

      const unreadItemCount = messages.filter(({ isRead }) => !isRead).length;
      if (unreadItemCount === 0) {
        return state;
      }
      return {
        ...state,
        ...(originMailFolder
          ? {
              [originMailFolder.id]: {
                ...originMailFolder,
                unreadItemCount:
                  originMailFolder?.unreadItemCount > 0
                    ? originMailFolder?.unreadItemCount - unreadItemCount
                    : 0,
              },
            }
          : {}),
        ...(destinationMailFolder
          ? {
              [destinationMailFolder.id]: {
                ...destinationMailFolder,
                unreadItemCount:
                  destinationMailFolder?.unreadItemCount >= 0
                    ? destinationMailFolder?.unreadItemCount + unreadItemCount
                    : 0,
              },
            }
          : {}),
      };
    }

    case COPY_MAIL_ME_TO_PROJECT_COMMIT: {
      const {
        meta: { destinationMailFolderId, messages },
        payload,
      } = action;
      const destinationMailFolder =
        state[
          destinationMailFolderId === 'inbox'
            ? payload[0]?.parentFolderId
            : destinationMailFolderId
        ];

      const unreadItemCount = messages.filter(({ isRead }) => !isRead).length;
      if (unreadItemCount === 0) {
        return state;
      }
      return {
        ...state,
        ...(destinationMailFolder
          ? {
              [destinationMailFolder.id]: {
                ...destinationMailFolder,
                unreadItemCount:
                  destinationMailFolder?.unreadItemCount >= 0
                    ? destinationMailFolder?.unreadItemCount + unreadItemCount
                    : 0,
              },
            }
          : {}),
      };
    }

    case WS_EMAIL_PROJECT_CREATED: {
      const {
        payload: { message },
        meta: { wsMailFolderId, messageAlreadyExists },
      } = action;
      if (messageAlreadyExists) {
        return state;
      }
      const mailFolder = state[wsMailFolderId];
      return {
        ...state,
        ...(mailFolder
          ? {
              [mailFolder.id]: {
                ...mailFolder,
                unreadItemCount: !message.isRead
                  ? mailFolder?.unreadItemCount >= 0
                    ? mailFolder?.unreadItemCount + 1
                    : 0
                  : mailFolder?.unreadItemCount,
              },
            }
          : {}),
      };
    }

    case WS_REVERT_MESSAGE_MOVE_PROJECT: {
      const { destinationId, message } = action;
      if (!(message as Message).isRead) {
        const sourceId = message.parentFolderId;

        const sourceFolder = state[sourceId];
        const destinationFolder = state[destinationId];
        return {
          ...state,
          ...(sourceFolder
            ? {
                [sourceId]: {
                  ...sourceFolder,
                  unreadItemCount: sourceFolder.unreadItemCount + 1,
                },
              }
            : {}),
          ...(destinationFolder
            ? {
                [destinationId]: {
                  ...destinationFolder,
                  unreadItemCount:
                    destinationFolder.unreadItemCount === 0
                      ? 0
                      : destinationFolder.unreadItemCount - 1,
                },
              }
            : {}),
        };
      }
      return state;
    }

    case WS_REVERT_MESSAGE_DELETE_PROJECT: {
      const { message } = action;
      if (!(message as Message).isRead) {
        const sourceId = message.parentFolderId;
        const sourceFolder = state[sourceId];
        return {
          ...state,
          ...(sourceFolder
            ? {
                [sourceId]: {
                  ...sourceFolder,
                  unreadItemCount: sourceFolder.unreadItemCount + 1,
                },
              }
            : {}),
        };
      }
      return state;
    }

    case RENAME_MAIL_FOLDER_COMMIT: {
      const {
        meta: { mailFolderId, newDisplayName },
      } = action;
      const mailFolder = state[mailFolderId];
      return {
        ...state,
        [mailFolderId]: {
          ...mailFolder,
          displayName: newDisplayName,
        },
      };
    }

    case RENAME_MAIL_FOLDER_ROLLBACK: {
      const {
        meta: { mailFolderId, oldDisplayName },
      } = action;
      const mailFolder = state[mailFolderId];
      return {
        ...state,
        [mailFolderId]: {
          ...mailFolder,
          displayName: oldDisplayName,
        },
      };
    }

    case CLEAR_PRIO_CACHE: {
      return {};
    }

    case MARK_AS_READ_COMMIT: {
      const {
        meta: { mailFolderId, changedUnreadItemCount, isRead },
      } = action;

      const mailFolder = state[mailFolderId];

      if (mailFolder) {
        const unreadItemCount =
          mailFolder.unreadItemCount +
          changedUnreadItemCount * (isRead ? -1 : 1);
        return {
          ...state,
          [mailFolderId]: {
            ...mailFolder,
            unreadItemCount: unreadItemCount >= 0 ? unreadItemCount : 0,
          },
        };
      }

      return state;
    }

    default:
      return state;
  }
};

const mapMailFolderToIdEntry = (mailFolders: Array<MailFolder>) =>
  mailFolders.map((item) => ({
    id: item.id,
    ...(item.childFolder
      ? { childFolder: mapMailFolderToIdEntry(item.childFolder) }
      : {}),
  }));

export interface ByProjectId {
  [key: string]: Array<MailFolderIdEntry>;
}

const ids: Reducer<ByProjectId, any> = (state = {}, action) => {
  switch (action.type) {
    case FETCH_PROJECT_MAIL_FOLDERS_COMMIT: {
      const {
        payload,
        meta: { projectId },
      } = action;
      return {
        ...state,
        [projectId]: mapMailFolderToIdEntry(payload as Array<MailFolder>),
      };
    }

    case CLEAR_PRIO_CACHE: {
      return {};
    }
    default:
      return state;
  }
};

interface MailFoldersMeta {
  isFetching: boolean;
  hasError: boolean;
  errorMessage?: string;
  fetchingProjectIds: ProjectId[];
}

const meta: Reducer<MailFoldersMeta, any> = (
  state = { isFetching: false, hasError: false, fetchingProjectIds: [] },
  action
) => {
  switch (action.type) {
    case FETCH_PROJECT_MAIL_FOLDERS_REQUEST: {
      const {
        meta: { projectId },
      } = action;
      return {
        ...state,
        isFetching: true,
        fetchingProjectIds: distinct([...state.fetchingProjectIds, projectId]),
      };
    }
    case FETCH_PROJECT_MAIL_FOLDERS_COMMIT: {
      const {
        meta: { projectId },
      } = action;
      return {
        ...state,
        isFetching: false,
        fetchingProjectIds: state.fetchingProjectIds.filter(
          (id) => id !== projectId
        ),
      };
    }
    case FETCH_PROJECT_MAIL_FOLDERS_ROLLBACK: {
      const {
        meta: { projectId },
      } = action;
      return {
        ...state,
        isFetching: false,
        hasError: true,
        errorMessage: 'mail:errorMessages.mailFolders.fetchError',
        fetchingProjectIds: state.fetchingProjectIds.filter(
          (id) => id !== projectId
        ),
      };
    }

    case DELETE_LOCAL_MESSAGES:
    case SAGA_REBUILD:
    case CLEAR_PRIO_CACHE: {
      return { isFetching: false, hasError: false, fetchingProjectIds: [] };
    }
    default:
      return state;
  }
};

interface ActiveMailFolderState {
  [projectId: ProjectId]: MailFolderId;
}

const activeMailFolder: Reducer<
  ActiveMailFolderState,
  ActiveMailFolderAction
> = (state = {}, action) => {
  switch (action.type) {
    case UPDATE_ACTIVE_MAILFOLDER_PROJECT: {
      const { projectId, mailFolderId } = action;
      return {
        ...state,
        [projectId]: mailFolderId,
      };
    }
    case CLEAR_PRIO_CACHE: {
      return {};
    }
    default:
      return state;
  }
};

interface OpenMailFolderKeysState {
  [projectId: ProjectId]: MailFolderId[];
}

const openMailFolderKeys: Reducer<OpenMailFolderKeysState, ReduxAction> = (
  state = {},
  action
) => {
  switch (action.type) {
    case FETCH_PROJECT_MAIL_FOLDERS_COMMIT: {
      const {
        meta: { projectId },
      } = action;
      if (!state[projectId]) {
        return {
          ...state,
          [projectId]: [],
        };
      }
      return state;
    }
    case SET_OPEN_MAILFOLDER_KEYS_PROJECT: {
      const { projectId, mailFolderIds } = action;
      return {
        ...state,
        [projectId]: mailFolderIds,
      };
    }
    case ADD_OPEN_MAILFOLDER_KEY_PROJECT: {
      const { projectId, mailFolderId } = action;
      if (!mailFolderId || state[projectId]?.includes(mailFolderId)) {
        return state;
      }
      return {
        ...state,
        [projectId]: [...(state[projectId] ?? []), mailFolderId],
      };
    }
    case CLEAR_PRIO_CACHE: {
      return {};
    }
    default: {
      return state;
    }
  }
};

export default combineReducers<MailFoldersState>({
  byId,
  ids,
  meta,
  activeMailFolder,
  openMailFolderKeys,
});

const expand = (ids: MailFolderIdEntry[], byId: MailFolderByMailFolderId) =>
  ids.map(({ id, childFolder }) => ({
    ...byId[id],
    ...(childFolder ? { childFolder: expand(childFolder, byId) } : {}),
  }));

export const getAllMailFolders: (
  state: MailFoldersState,
  projectId: string
) => Array<MailFolder> = (state, projectId) =>
  expand(state.ids[projectId] ?? [], state.byId);

export const getMailFoldersById: (
  state: MailFoldersState
) => MailFolderByMailFolderId = (state) => state.byId ?? {};

export const getMailFolder: (
  state: MailFoldersState,
  id: string
) => MailFolder = (state, id) => state.byId[id];

export const getIsFetching: (
  state: MailFoldersState,
  projectId: ProjectId
) => boolean = (state, projectId) => {
  if (projectId === 'favorites') {
    return state.meta.isFetching;
  }
  return state.meta.fetchingProjectIds.includes(projectId);
};
export const getHasError: (state: MailFoldersState) => boolean = (state) =>
  state.meta.hasError;
export const getErrorMessage: (state: MailFoldersState) => string = (state) =>
  state.meta.errorMessage;

export const getActiveMailFolderId: (
  state: MailFoldersState,
  projectId: ProjectId
) => string = (state, projectId) => state.activeMailFolder[projectId] ?? null;

export const getOpenMailFolderKeys: (
  state: MailFoldersState,
  projectId: ProjectId
) => MailFolderId[] = (state, projectId) => {
  if (state.openMailFolderKeys[projectId]) {
    return state.openMailFolderKeys[projectId];
  }
  return [];
};
